// calendar-week-grid-derive.ts — pure helpers for the hour-grid week view (D11 full).
// Time ↔ pixel math + event block geometry. No React, no side effects → unit-testable.

/** Pixel height of one hour row in the grid. */
export const GRID_HOUR_HEIGHT = 44;
/** Hours rendered top→bottom (0..23). */
export const GRID_HOURS: number[] = Array.from({ length: 24 }, (_, h) => h);
/** Total grid content height in px. */
export const GRID_TOTAL_HEIGHT = GRID_HOUR_HEIGHT * 24;
/** Default scroll target (px) on mount → ~06:00 so the working day is visible. */
export const GRID_DEFAULT_SCROLL = GRID_HOUR_HEIGHT * 6;

const pad = (n: number) => String(n).padStart(2, "0");

/** "HH:MM" → minutes since 00:00 (NaN-safe → 0). */
export function timeToMinutes(time: string): number {
  const [h, m] = (time ?? "").split(":").map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

/** minutes since 00:00 → "HH:MM", clamped to [00:00, 23:59]. */
export function minutesToTime(min: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(min)));
  return `${pad(Math.floor(clamped / 60))}:${pad(clamped % 60)}`;
}

/**
 * Convert a vertical pixel offset within the grid content to a snapped "HH:MM".
 * `snapMin` rounds to the nearest block (default 30 minutes) so drops land tidy.
 */
export function offsetToTime(y: number, snapMin = 30): string {
  const rawMin = (y / GRID_HOUR_HEIGHT) * 60;
  const snapped = Math.round(rawMin / snapMin) * snapMin;
  return minutesToTime(snapped);
}

/** Top px of an event block from its start time. */
export function eventTop(time: string): number {
  return (timeToMinutes(time) / 60) * GRID_HOUR_HEIGHT;
}

/** Height px of an event block from its duration (min 18px so short blocks stay tappable). */
export function eventHeight(durationMin?: number): number {
  const d = durationMin && durationMin > 0 ? durationMin : 30;
  return Math.max(18, (d / 60) * GRID_HOUR_HEIGHT);
}

/**
 * P6.3: assign side-by-side columns to overlapping events so they don't render on
 * top of each other. Greedy interval colouring per overlap cluster. Each result
 * gets `col` (0-based) + `cols` (total columns in its cluster) → caller maps to
 * left/width percentages.
 */
export type Overlapped<T> = T & { col: number; cols: number };

export function layoutOverlaps<T extends { startMin: number; endMin: number }>(items: T[]): Overlapped<T>[] {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const result: Overlapped<T>[] = [];
  let group: Overlapped<T>[] = [];
  let colEnds: number[] = []; // end-min of the last event placed in each column
  let groupEnd = -Infinity;

  const flush = () => {
    const cols = group.reduce((m, g) => Math.max(m, g.col + 1), 1);
    for (const g of group) g.cols = cols;
    result.push(...group);
    group = [];
    colEnds = [];
    groupEnd = -Infinity;
  };

  for (const it of sorted) {
    if (group.length && it.startMin >= groupEnd) flush();
    let col = colEnds.findIndex((end) => end <= it.startMin);
    if (col === -1) { col = colEnds.length; colEnds.push(it.endMin); }
    else colEnds[col] = it.endMin;
    group.push({ ...it, col, cols: 1 });
    groupEnd = Math.max(groupEnd, it.endMin);
  }
  if (group.length) flush();
  return result;
}
